/**
 * Focus-trap helpers for modal dialogs.
 *
 * `findFocusable(root)` is a pure DOM walk — extracted so the trap logic
 * can be unit-tested without React. The hook in `useFocusTrap.ts` wires
 * it up to a ref and Tab/Shift+Tab key handling.
 *
 * "Focusable" here means: visible, not disabled, with one of the standard
 * tab-stop tag names, or with an explicit tabindex >= 0. We deliberately
 * skip elements with tabindex=-1 (programmatic focus only) and any
 * `inert` ancestor.
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',');

/**
 * Return every focusable descendant of `root`, in DOM order. Skips
 * elements that are visually hidden (`display:none` / `visibility:hidden`)
 * because Tab can't reach them anyway.
 */
export function findFocusable(root: HTMLElement): HTMLElement[] {
  const candidates = Array.from(
    root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  );
  return candidates.filter((el) => isVisible(el));
}

function isVisible(el: HTMLElement): boolean {
  if (el.hasAttribute('hidden')) return false;
  if (!el.isConnected) return false;
  if (el.style.display === 'none' || el.style.visibility === 'hidden') {
    return false;
  }
  return true;
}

/**
 * Compute where focus should land after Tab from `current`. Pure function
 * — separated so the wrap-around logic is unit-testable.
 *
 * @param items Focusable elements in DOM order. Must be non-empty.
 * @param current Currently focused element, or null if focus is outside.
 * @param shift True for Shift+Tab.
 * @returns The element to focus next, or `null` if `items` is empty.
 */
export function nextFocusTarget(
  items: HTMLElement[],
  current: HTMLElement | null,
  shift: boolean,
): HTMLElement | null {
  if (items.length === 0) return null;
  const idx = current ? items.indexOf(current) : -1;
  if (idx === -1) {
    // Focus is outside the trap — pull it back to the first/last item.
    return shift ? items[items.length - 1]! : items[0]!;
  }
  if (shift) {
    return idx === 0 ? items[items.length - 1]! : items[idx - 1]!;
  }
  return idx === items.length - 1 ? items[0]! : items[idx + 1]!;
}
